import { useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Access-requests client-side API: the hotel-portal access requests submitted
 * on the marketing site (hotel leads), listed and reviewed by internal staff.
 */

/** Access request statuses (mirror of the API's HotelLeadStatus enum). */
export const ACCESS_REQUEST_STATUSES = {
  pending: 'PENDING',
  approved: 'APPROVED',
  denied: 'DENIED',
} as const;

export type AccessRequestStatus =
  (typeof ACCESS_REQUEST_STATUSES)[keyof typeof ACCESS_REQUEST_STATUSES];

/** Review actions accepted by the review endpoint. */
export const ACCESS_REQUEST_REVIEW_ACTIONS = {
  approve: 'approve',
  deny: 'deny',
} as const;

export type AccessRequestReviewAction =
  (typeof ACCESS_REQUEST_REVIEW_ACTIONS)[keyof typeof ACCESS_REQUEST_REVIEW_ACTIONS];

/** One access request row (API DTO — never includes the magic-link token). */
export interface AccessRequest {
  id: string;
  email: string;
  domain: string;
  status: AccessRequestStatus;
  accessReason: string | null;
  isVerified: boolean;
  verifiedAt: string | null;
  requestedAt: string;
  statusChangedAt: string | null;
  reviewedAt: string | null;
  reviewedByEmail: string | null;
  lastAccessedAt: string | null;
  accessCount: number;
}

export interface AccessRequestsPagination {
  total: number;
  page: number;
  limit: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface AccessRequestsResult {
  requests: AccessRequest[];
  counts: Record<AccessRequestStatus, number>;
  pagination: AccessRequestsPagination;
}

export interface AccessRequestsFilters {
  status?: AccessRequestStatus;
  search?: string;
  page?: number;
  limit?: number;
}

const ACCESS_REQUESTS_QUERY_KEY = ['AccessRequests'] as const;

const accessRequestsListKey = (filters: AccessRequestsFilters) =>
  [...ACCESS_REQUESTS_QUERY_KEY, 'list', filters] as const;

/**
 * List hotel-portal access requests with per-status counts (for the tab
 * badges) and pagination.
 *
 * @param filters - Status filter, email/domain search, and pagination.
 * @returns Query resolving to requests + counts + pagination.
 */
export function useAccessRequests(filters: AccessRequestsFilters = {}) {
  const apiClient = useApiClient();

  return useQuery<AccessRequestsResult>({
    queryKey: accessRequestsListKey(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      if (filters.page) params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      const query = params.toString();
      const response = await apiClient.get<{
        success: boolean;
        data: AccessRequest[];
        counts: Record<AccessRequestStatus, number>;
        pagination: AccessRequestsPagination;
      }>(`/hotel-leads${query ? `?${query}` : ''}`);
      return {
        requests: response.data,
        counts: response.counts,
        pagination: response.pagination,
      };
    },
  });
}

export interface ReviewAccessRequestInput {
  id: string;
  action: AccessRequestReviewAction;
}

export interface ReviewAccessRequestResult {
  request: AccessRequest;
  /** False when the request was already in the target state (idempotent repeat). */
  transitioned: boolean;
}

/**
 * Approve or deny an access request. Approving emails the requester their
 * portal link and whitelists them in HubSpot; denying revokes any outstanding
 * link. Invalidates every access-requests list on success.
 *
 * @returns Mutation taking `{ id, action }`.
 */
export function useReviewAccessRequest() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<ReviewAccessRequestResult, Error, ReviewAccessRequestInput>({
    mutationFn: async ({ id, action }) => {
      const response = await apiClient.put<{
        success: boolean;
        data: AccessRequest;
        transitioned: boolean;
      }>(`/hotel-leads/${id}/review`, { action });
      return { request: response.data, transitioned: response.transitioned };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCESS_REQUESTS_QUERY_KEY });
    },
  });
}
